<template>
  <div>
    <Loading v-if="cargando" />
    <div class="container-fluid">
      <!-- Filtros de Fechas -->
      <div class="card">
        <div class="card-header bg-primary">
          <h3 class="card-title text-white">
            <i class="fas fa-calendar-alt mr-2"></i>
            Rango de Fechas
          </h3>
          <div class="card-tools">
            <button
              type="button"
              class="btn btn-sm btn-light"
              @click="limpiarTodo()"
              v-if="Object.keys(volumenes).length > 0"
              :disabled="estado == 2"
            >
              <i class="fas fa-broom mr-1"></i>
              Limpiar Volúmenes
            </button>
          </div>
        </div>

        <div class="card-body">
          <div class="alert alert-info">
            <i class="fas fa-info-circle mr-2"></i>
            <strong>Fechas Maestras:</strong>
            <span v-if="fechaInicialMaestra && fechaFinalMaestra">
              {{ formatearFecha(fechaInicialMaestra) }} -
              {{ formatearFecha(fechaFinalMaestra) }}
            </span>
            <span v-else class="text-warning">
              No hay fechas maestras definidas. Defina las fechas en el
              formulario principal.
            </span>
          </div>
          <div class="row">
            <div class="form-group col-md-4">
              <label for="fecha_inicial">Fecha Inicial</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="fecha_inicial"
                @change="calcularDias()"
                :class="
                  fecha_inicial && esFechaInicialValida()
                    ? 'is-valid'
                    : 'is-invalid'
                "
                :min="fechaInicialMaestra"
                :max="fechaFinalMaestra"
                :disabled="estado == 2"
                :title="
                  fechaInicialMaestra
                    ? `Debe ser mayor o igual a ${formatearFecha(
                        fechaInicialMaestra
                      )}`
                    : ''
                "
              />
              <small v-if="fechaInicialMaestra" class="form-text text-muted">
                Mínimo: {{ formatearFecha(fechaInicialMaestra) }}
              </small>
            </div>
            <div class="form-group col-md-4">
              <label for="fecha_final">Fecha Final</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="fecha_final"
                @change="calcularDias()"
                :class="
                  fecha_final && esFechaFinalValida()
                    ? 'is-valid'
                    : 'is-invalid'
                "
                :min="fechaInicialMaestra"
                :max="fechaFinalMaestra"
                :disabled="estado == 2"
                :title="
                  fechaFinalMaestra
                    ? `Debe ser menor o igual a ${formatearFecha(
                        fechaFinalMaestra
                      )}`
                    : ''
                "
              />
              <small v-if="fechaFinalMaestra" class="form-text text-muted">
                Máximo: {{ formatearFecha(fechaFinalMaestra) }}
              </small>
            </div>
            <div class="form-group col-md-4">
              <label>Total de Días</label>
              <input
                type="number"
                class="form-control form-control-sm"
                :value="totalDias"
                readonly
                :class="totalDias > 0 ? 'is-valid' : 'is-invalid'"
              />
            </div>
          </div>
          <div
            v-if="fechasDelRango && fechasDelRango.length > 0"
            class="alert alert-secondary py-2 mt-2 mb-0"
          >
            <i class="fas fa-list-ol mr-2"></i>
            <strong>Posiciones por fecha:</strong>
            <span class="ml-2">
              <span
                v-for="(f, i) in fechasDelRango"
                :key="f"
                class="badge badge-light mr-1"
                :title="formatearFecha(f)"
              >
                {{ i + 1 }}: {{ formatearFecha(f) }}
              </span>
            </span>
          </div>

          <!-- Lista de Bloques y Clientes (solo volúmenes netos) -->
          <div class="card mt-3" v-if="bloques && bloques.length">
            <div class="card-header bg-navy">
              <h3 class="card-title text-white">
                <i class="fas fa-map-marked-alt mr-2"></i>
                Bloques y por Cliente (Volúmenes netos)
              </h3>
              <button
                type="button"
                class="btn btn-xs btn-success float-right"
                @click.stop="saveVolumenes()"
                :disabled="!puedeGuardar || estado == 2"
              >
                <i class="fas fa-save mr-2"></i>
                Guardar
              </button>
            </div>
            <div class="card-body">
              <div v-for="bloque in bloques" :key="bloque.id" class="mb-3">
                <!-- Encabezado del Bloque -->
                <div
                  class="card-header bg-geogo-top-left p-2"
                  @click="estado == 2 ? null : toggleBloque(bloque.id)"
                  :style="estado == 2 ? 'cursor: default;' : 'cursor: pointer;'"
                >
                  <h6
                    class="mb-0 text-white d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <i class="fas fa-building mr-2"></i>
                      {{ bloque.nombre }}
                    </span>
                    <i
                      :class="
                        isBloqueExpanded(bloque.id)
                          ? 'fas fa-chevron-down'
                          : 'fas fa-chevron-right'
                      "
                    ></i>
                  </h6>
                </div>

                <!-- Tabla: un input por cliente; el valor se aplica a todas las posiciones del rango (ej: 80 del 1 al 15 = 15 posiciones con 80) -->
                <div
                  class="table-responsive"
                  v-show="isBloqueExpanded(bloque.id)"
                  v-if="clientesLista && clientesLista.length"
                >
                  <div
                    v-if="!fechasDelRango || fechasDelRango.length === 0"
                    class="p-3 text-center text-warning"
                  >
                    <i class="fas fa-calendar-check mr-2"></i>
                    Seleccione fecha inicial y fecha final. El valor que ingrese
                    se creará en cada posición del rango (ej: del 1 al 15 con 80
                    = 15 posiciones con valor 80).
                  </div>
                  <table
                    v-else
                    class="table table-bordered table-striped table-hover table-sm mb-0"
                  >
                    <thead class="bg-dark text-white">
                      <tr>
                        <th class="text-center">Cliente</th>
                        <th class="text-center">
                          Volumen (se aplica a las
                          {{ fechasDelRango.length }} posiciones)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <template
                        v-for="(cliente, clienteIndex) in clientesLista"
                      >
                        <tr
                          :key="`cliente-${clienteIndex}`"
                          data-widget="expandable-table"
                          aria-expanded="false"
                        >
                          <td class="align-middle">
                            <strong>{{ cliente.razon_social }}</strong>
                          </td>
                          <td class="align-middle">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              class="form-control form-control-sm text-right"
                              :value="getVolumenNeto(bloque.id, cliente.id)"
                              @input="
                                setVolumenNeto(
                                  bloque.id,
                                  cliente.id,
                                  $event.target.value
                                )
                              "
                              @click.stop
                              :disabled="estado == 2"
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                        <tr
                          :key="`expand-${clienteIndex}`"
                          class="expandable-body d-none"
                        >
                          <td colspan="5" class="p-0">
                            <div class="p-3">
                              <h6 class="mb-3">
                                <i class="fas fa-list mr-2"></i>
                                Detalle de Volúmenes por Fecha
                              </h6>
                              <div
                                v-if="
                                  $parent.form.det_programaciones_clientes
                                    .length > 0
                                "
                                class="table-responsive"
                              >
                                <table
                                  class="table table-sm table-bordered table-striped"
                                >
                                  <thead class="bg-secondary text-white">
                                    <tr>
                                      <th class="text-center">Fecha</th>
                                      <th class="text-center">Volumen Día</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr
                                      v-for="(dato, datoIndex) in $parent.form
                                        .det_programaciones_clientes"
                                      :key="`${clienteIndex}-${datoIndex}`"
                                    >
                                      <td
                                        class="align-middle text-center"
                                        v-if="
                                          dato.cliente_id === cliente.id &&
                                          dato.bloque_id === bloque.id
                                        "
                                      >
                                        {{ formatearFecha(dato.fecha) }}
                                      </td>
                                      <td
                                        class="align-middle text-right"
                                        v-if="
                                          dato.cliente_id === cliente.id &&
                                          dato.bloque_id === bloque.id
                                        "
                                      >
                                        {{ formatNumber(dato.volumen_dia) }}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <div v-else class="alert alert-info mb-0">
                                <i class="fas fa-info-circle mr-2"></i>
                                No hay datos registrados para este cliente.
                              </div>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                    <tfoot class="bg-light">
                      <tr>
                        <th class="text-right">Total del bloque:</th>
                        <th class="text-right">
                          {{ formatNumber(getTotalBloque(bloque.id)) }}
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div
                  v-else
                  v-show="isBloqueExpanded(bloque.id)"
                  class="p-3 text-center text-muted"
                >
                  <i class="fas fa-info-circle mr-2"></i>
                  No hay clientes para asignar volúmenes.
                </div>
              </div>
            </div>
          </div>
          <!-- Mensaje cuando no hay bloques -->
          <div class="card mt-3" v-if="!cargando && bloques.length === 0">
            <div class="card-body text-center p-5">
              <i class="fas fa-info-circle fa-3x text-muted mb-3"></i>
              <h5 class="text-muted">No hay bloques disponibles</h5>
              <p class="text-muted">
                No se encontraron bloques para cargar volúmenes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "../../../../components/Loading";
import axios from "axios";

export default {
  name: "CalculoVolumenBloques",
  components: {
    Loading,
  },
  props: {
    fechaInicialMaestra: {
      type: String,
      default: "",
    },
    fechaFinalMaestra: {
      type: String,
      default: "",
    },
    programacionId: {
      type: [Number, String],
      default: null,
    },
    estado: {
      type: [Number, String],
      default: 1,
    },
    detProgramacionesClientes: {
      type: Array,
      default: () => [],
    },
    clientes: {
      type: Array,
      default: () => [],
    },
    bloques: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      cargando: false,
      fecha_inicial: "",
      fecha_final: "",
      totalDias: 0,
      volumenes: {}, // clave "bloqueId-clienteId-fecha" -> volumen neto (por fecha)
      registrosVolumen: [], // array de { bloque_id, cliente_id, fecha, volumen }
      bloquesExpandidos: {}, // { bloque_id: true/false }
      estadoDesdeDb: null, // estado cargado desde la base de datos para el bloqueo
    };
  },
  watch: {
    fechaInicialMaestra: {
      handler(newVal) {
        if (newVal) {
          this.fecha_inicial = newVal;
          this.calcularDias();
        }
      },
      immediate: true,
    },
    fechaFinalMaestra: {
      handler(newVal) {
        if (newVal) {
          this.fecha_final = newVal;
          this.calcularDias();
        }
      },
      immediate: true,
    },
    // Cargar volúmenes existentes por bloque, cliente y fecha (para edición)
    detProgramacionesClientes: {
      handler(detalle) {
        this.cargarVolumenesExistentes(detalle);
      },
      immediate: true,
      deep: true,
    },
    // Obtener estado desde la base de datos para bloquear cuando estado === 2
    /*  programacionId: {
      handler(id) {
        if (id) {
          this.obtenerEstadoDesdeDb();
        } else {
          this.estadoDesdeDb = null;
        }
      },
      immediate: true,
    }, */
  },
  mounted() {},
  methods: {
    calcularDias() {
      // Usar fechas maestras si están disponibles, sino usar las locales
      const fechaInicialStr = this.fecha_inicial;
      const fechaFinalStr = this.fecha_final;

      if (fechaInicialStr && fechaFinalStr) {
        const fechaInicial = new Date(fechaInicialStr);
        const fechaFinal = new Date(fechaFinalStr);

        // Validar que la fecha inicial no sea mayor que la fecha final
        if (fechaInicial > fechaFinal) {
          this.$swal({
            icon: "error",
            title: "La fecha inicial no puede ser mayor a la fecha final",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
          if (!this.fechaFinalMaestra) {
            this.fecha_final = "";
          }
          this.totalDias = 0;
          return;
        }

        // Validar que las fechas no superen el rango de las fechas maestras
        if (this.fechaInicialMaestra && this.fechaFinalMaestra) {
          const fechaInicialMaestra = new Date(this.fechaInicialMaestra);
          const fechaFinalMaestra = new Date(this.fechaFinalMaestra);

          if (fechaInicial < fechaInicialMaestra) {
            this.$swal({
              icon: "error",
              title:
                "La fecha inicial no puede ser menor a la fecha maestra inicial",
              text: `La fecha inicial debe ser mayor o igual a ${this.formatearFecha(
                this.fechaInicialMaestra
              )}`,
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 4000,
              timerProgressBar: true,
            });
            this.fecha_inicial = this.fechaInicialMaestra;
            return;
          }

          if (fechaFinal > fechaFinalMaestra) {
            this.$swal({
              icon: "error",
              title:
                "La fecha final no puede ser mayor a la fecha maestra final",
              text: `La fecha final debe ser menor o igual a ${this.formatearFecha(
                this.fechaFinalMaestra
              )}`,
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 4000,
              timerProgressBar: true,
            });
            this.fecha_final = this.fechaFinalMaestra;
            return;
          }
        }

        // Calcular diferencia en días (incluyendo ambos días)
        const diferencia = fechaFinal.getTime() - fechaInicial.getTime();
        this.totalDias = Math.floor(diferencia / (1000 * 60 * 60 * 24)) + 1;
      } else {
        this.totalDias = 0;
      }
    },

    getFechaInicialValida() {
      return this.fechaInicialMaestra || this.fecha_inicial;
    },

    getFechaFinalValida() {
      return this.fechaFinalMaestra || this.fecha_final;
    },

    getVolumenNeto(bloqueId, clienteId, fecha) {
      const fechas = this.obtenerFechasDelRango();
      const f =
        fecha != null
          ? this.normalizarFechaStr(fecha)
          : fechas.length > 0
          ? fechas[0]
          : this.fecha_inicial || "";
      const key = `${bloqueId}-${clienteId}-${f}`;
      return this.volumenes[key] != null ? Number(this.volumenes[key]) : 0;
    },

    // Sin fecha: aplica el valor a TODAS las posiciones del rango (ej: 80 del 1 al 15 = 15 posiciones con 80)
    setVolumenNeto(bloqueId, clienteId, value, fecha) {
      const volumen = parseFloat(value) || 0;
      const valorFinal = volumen >= 0 ? volumen : 0;
      const fechas = this.obtenerFechasDelRango();

      if (fecha != null) {
        const f = this.normalizarFechaStr(fecha);
        if (!f) return;
        const key = `${bloqueId}-${clienteId}-${f}`;
        this.$set(this.volumenes, key, valorFinal);
        this.actualizarRegistrosVolumen(bloqueId, clienteId, f, valorFinal);
        return;
      }

      if (fechas.length === 0) {
        const f = this.fecha_inicial || "";
        if (!f) return;
        const key = `${bloqueId}-${clienteId}-${f}`;
        this.$set(this.volumenes, key, valorFinal);
        this.actualizarRegistrosVolumen(bloqueId, clienteId, f, valorFinal);
        return;
      }

      // Aplicar el mismo valor a todas las fechas del rango (crear N posiciones)
      fechas.forEach((f) => {
        const key = `${bloqueId}-${clienteId}-${f}`;
        this.$set(this.volumenes, key, valorFinal);
        this.actualizarRegistrosVolumen(bloqueId, clienteId, f, valorFinal);
      });
    },

    // Busca si ya existe un registro para bloque, cliente y fecha
    existeRegistroVolumen(bloqueId, clienteId, fecha) {
      return this.registrosVolumen.some(
        (r) =>
          r.bloque_id === bloqueId &&
          r.cliente_id === clienteId &&
          r.fecha === this.normalizarFechaStr(fecha)
      );
    },

    // Mantiene el array registrosVolumen: si ya existe la fecha, actualiza; si no, agrega
    actualizarRegistrosVolumen(bloqueId, clienteId, fecha, volumen) {
      const fechaNorm = this.normalizarFechaStr(fecha);
      const idx = this.registrosVolumen.findIndex(
        (r) =>
          r.bloque_id === bloqueId &&
          r.cliente_id === clienteId &&
          r.fecha === fechaNorm
      );
      if (volumen > 0) {
        const existente = idx >= 0 ? this.registrosVolumen[idx] : null;
        const posicion = this.calcularPosicionParaFecha(fechaNorm);
        const registro = {
          bloque_id: bloqueId,
          cliente_id: clienteId,
          fecha: fechaNorm,
          volumen,
          cantidad_dias: this.totalDias || 0,
          posicion:
            posicion != null
              ? posicion
              : (existente && existente.posicion) || null,
          id: existente && existente.id != null ? existente.id : null,
        };
        if (idx >= 0) {
          this.$set(this.registrosVolumen, idx, registro);
        } else {
          this.registrosVolumen.push(registro);
        }
      } else {
        if (idx >= 0) {
          this.registrosVolumen.splice(idx, 1);
        }
      }
    },

    // Normaliza fecha a YYYY-MM-DD
    normalizarFechaStr(fecha) {
      if (!fecha) return "";
      const str =
        typeof fecha === "string" ? fecha : (fecha && fecha.fecha) || "";
      if (str.includes("T")) return str.split("T")[0];
      return str;
    },

    // Genera el array de fechas del rango (una posición por día)
    obtenerFechasDelRango() {
      if (!this.fecha_inicial || !this.fecha_final) return [];
      const fechas = [];
      const ini = new Date(this.fecha_inicial + "T00:00:00");
      const fin = new Date(this.fecha_final + "T00:00:00");
      if (ini > fin) return [];
      const actual = new Date(ini);
      while (actual <= fin) {
        const y = actual.getFullYear();
        const m = String(actual.getMonth() + 1).padStart(2, "0");
        const d = String(actual.getDate()).padStart(2, "0");
        fechas.push(`${y}-${m}-${d}`);
        actual.setDate(actual.getDate() + 1);
      }
      return fechas;
    },

    // Calcula el número de posición (1-based) de una fecha dentro del rango actual
    calcularPosicionParaFecha(fecha) {
      const fechaNorm = this.normalizarFechaStr(fecha);
      if (!fechaNorm || !this.fecha_inicial || !this.fecha_final) return null;
      const fechas = this.obtenerFechasDelRango();
      const idx = fechas.indexOf(fechaNorm);
      return idx >= 0 ? idx + 1 : null;
    },

    // Carga y pinta en volumenes/registrosVolumen el detalle de clientes del backend (para edición; conserva id para UPDATE)
    cargarVolumenesExistentes(detalle) {
      if (!detalle || !Array.isArray(detalle)) return;
      const items = detalle.filter(
        (d) =>
          d != null &&
          (d.bloque_id != null || d.bloque_id === 0) &&
          (d.cliente_id != null || d.cliente_id === 0) &&
          d.fecha
      );
      if (!items.length) return;

      // Limpiar y pintar solo los datos del backend para que se vean en los inputs
      this.volumenes = {};
      this.registrosVolumen = [];

      items.forEach((d) => {
        const fecha = this.normalizarFechaStr(d.fecha);
        const bloqueId = d.bloque_id;
        const clienteId = d.cliente_id;
        const vol = parseFloat(d.volumen_neto);
        const volumen = Number.isNaN(vol) ? 0 : vol >= 0 ? vol : 0;
        if (!fecha) return;

        const key = `${bloqueId}-${clienteId}-${fecha}`;
        this.$set(this.volumenes, key, volumen);

        const posicion =
          d.posicion != null
            ? d.posicion
            : this.calcularPosicionParaFecha(fecha);
        this.registrosVolumen.push({
          id: d.id != null ? d.id : null,
          bloque_id: bloqueId,
          cliente_id: clienteId,
          fecha,
          volumen,
          cantidad_dias:
            d.cantidad_dias != null ? d.cantidad_dias : this.totalDias || 0,
          posicion: posicion != null ? posicion : null,
        });
      });
    },

    // Total de un cliente en un bloque (suma de todas las posiciones/fechas del rango)
    getTotalClienteEnBloque(bloqueId, clienteId) {
      const fechas = this.obtenerFechasDelRango();
      return fechas.reduce(
        (sum, fecha) => sum + this.getVolumenNeto(bloqueId, clienteId, fecha),
        0
      );
    },

    // Total de una posición (fecha) en un bloque (suma de todos los clientes)
    getTotalPosicionBloque(bloqueId, fecha) {
      if (!this.clientes || !this.clientes.length) return 0;
      return this.clientes.reduce(
        (sum, c) => sum + this.getVolumenNeto(bloqueId, c.id, fecha),
        0
      );
    },

    getTotalBloque(bloqueId) {
      if (!this.clientes || !this.clientes.length) return 0;
      return this.clientes.reduce(
        (sum, c) => sum + this.getVolumenNeto(bloqueId, c.id),
        0
      );
    },

    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return "0.00";
      }
      return parseFloat(value)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    limpiarTodo() {
      this.$swal({
        title: "¿Está seguro?",
        text: "Se limpiarán todos los volúmenes netos ingresados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, limpiar",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.value) {
          this.volumenes = {};
          this.registrosVolumen = [];
          this.$swal({
            icon: "success",
            title: "Volúmenes limpiados",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
          });
        }
      });
    },

    toggleBloque(bloqueId) {
      const estadoActual = this.bloquesExpandidos[bloqueId] || false;
      this.$set(this.bloquesExpandidos, bloqueId, !estadoActual);
    },

    isBloqueExpanded(bloqueId) {
      return this.bloquesExpandidos[bloqueId] !== false;
    },

    // Métodos del Calendario

    esFechaInicialValida() {
      if (!this.fecha_inicial) return false;
      if (!this.fechaInicialMaestra) return true;

      const fechaInicial = new Date(this.fecha_inicial);
      const fechaInicialMaestra = new Date(this.fechaInicialMaestra);
      return fechaInicial >= fechaInicialMaestra;
    },

    esFechaFinalValida() {
      if (!this.fecha_final) return false;
      if (!this.fechaFinalMaestra) return true;

      const fechaFinal = new Date(this.fecha_final);
      const fechaFinalMaestra = new Date(this.fechaFinalMaestra);
      return fechaFinal <= fechaFinalMaestra;
    },

    formatearFecha(fecha) {
      if (!fecha) return "";
      const date = new Date(fecha + "T00:00:00");
      const dia = date.getDate();
      const mes = date.getMonth() + 1;
      const anio = date.getFullYear();
      return `${dia.toString().padStart(2, "0")}/${mes
        .toString()
        .padStart(2, "0")}/${anio}`;
    },

    // Construir payload de volúmenes por bloque, cliente, fecha, posición y rango para guardar (incluye id para actualizar)
    construirFormVolClientes() {
      if (!this.programacionId) return [];
      return this.registrosVolumen.map((r) => {
        const posicion =
          r.posicion != null
            ? r.posicion
            : this.calcularPosicionParaFecha(r.fecha);
        const item = {
          programacion_id: this.programacionId,
          bloque_id: r.bloque_id,
          cliente_id: r.cliente_id,
          fecha: r.fecha,
          volumen_neto: r.volumen,
          cantidad_dias: r.cantidad_dias,
          posicion: posicion != null ? posicion : null,
        };
        if (r.id != null) item.id = r.id;
        return item;
      });
    },

    saveVolumenes() {
      const formVolClientes = this.construirFormVolClientes();
      if (!formVolClientes.length) {
        this.$swal({
          title: "No hay volúmenes para guardar",
          text: "Ingrese al menos un volumen neto mayor a 0",
          icon: "warning",
        });
        return;
      }
      this.cargando = true;
      axios
        .post("/api/argentina/programaciones/saveDetProgramacion", {
          formVolClientes: formVolClientes,
          fecha_inicial: this.fecha_inicial,
          fecha_final: this.fecha_final,
        })
        .then(() => {
          this.cargando = false;
          this.$parent.init();
          this.$swal({
            title: "Se guardaron los volúmenes netos correctamente",
            icon: "success",
            confirmButtonText: "Ok!",
            timer: 3000,
            timerProgressBar: true,
          });
        })
        .catch(() => {
          this.$swal({
            title: "Ha ocurrido un error",
            icon: "error",
            confirmButtonText: "Ok!",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.cargando = false;
        });
    },
  },

  computed: {
    clientesLista() {
      const list = this.clientes || [];
      return Array.isArray(list)
        ? list.filter((c) => c != null && c.id != null)
        : [];
    },

    // Posiciones según las fechas del rango (cada día = una posición)
    fechasDelRango() {
      return this.obtenerFechasDelRango();
    },

    puedeGuardar() {
      if (!this.programacionId || this.programacionId <= 0) return false;
      const items = this.construirFormVolClientes();
      return items.length > 0;
    },
  },
};
</script>